import { useEffect, type ReactElement } from 'react';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { ChannelsSection } from '../contexts/distribution/channels';
import { EntityListPage, EntityPage, EntityTypesSection, PostFieldsSection } from '../contexts/content/entities';
import { ApiKeysAdminPage, LoginPage, ProfilePage, RegisterPage, SessionStatus, UsersAdminPage } from '../contexts/accounts/identity';
import { PlansAdminPage } from '../contexts/accounts/plans';
import { MediaLibraryPage, MediaPage, MediaStorageSection, MediaTrafficSection } from '../contexts/content/media';
import { PostFormatsSection, PostPage, PostsPage, StatusesSection } from '../contexts/content/posts';
import { DuePage, PublicationPage } from '../contexts/distribution/publishing';
import { CalendarPage, QueuePage, SchemesSection } from '../contexts/distribution/scheduling';
import { AdminBlogsPage, AdminOnlinePage, AdminOverviewPage } from '../contexts/platform/admin';
import { AdminSettingsPage } from '../contexts/platform/config';
import { AdminBackupsPage } from '../contexts/platform/operations';
import { AdminTasksPage, BlogTaskPage, BlogTasksSection } from '../contexts/platform/tasks';
import { BlogProvider, BlogSettingsSection, BlogsPage, MembersSection } from '../contexts/accounts/workspaces';
import { useResource } from '../shared/hooks';
import type { RouteMatch } from '../shared/router/router';
import { Alert } from '../ui-kit/Alert/Alert';
import { Button } from '../ui-kit/Button/Button';
import { EmptyState } from '../ui-kit/EmptyState/EmptyState';
import { Loading } from '../ui-kit/Loading/Loading';
import { PageHeader } from '../ui-kit/PageHeader/PageHeader';
import { NavTabs } from '../ui-kit/Tabs/Tabs';
import { AppShellConnected } from './AppShellConnected';
import { useNavigate, useRoute, useServices, useSession, useT } from './di';
import { BLOG_ROUTES, SettingsSection, type RouteKey } from './routes';

/** Корень SPA: охрана входа (IAM-UC-3.CC-1), выбор блога (WS-UC-2), страницы по маршруту. */
export function App(): ReactElement {
  const { session, router } = useServices();
  const { t } = useT();
  const state = useSession();
  const route = useRoute();
  const navigate = useNavigate();

  useEffect(() => {
    void session.load();
  }, [session]);

  // IAM-UC-3.CC-1: без сессии — на вход с возвратом на исходную страницу (регистрация открыта и без сессии).
  useEffect(() => {
    if (state.status !== SessionStatus.Anonymous || route?.key === 'login' || route?.key === 'register') return;
    const location = router.location.get();
    const next = `${location.pathname}${location.search}`;
    router.navigate(`${router.path('login')}${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`, { replace: true });
  }, [state.status, route?.key, router]);

  // Главная: последний блог или список блогов.
  useEffect(() => {
    if (state.status !== SessionStatus.Authenticated || route?.key !== 'home' || !state.me) return;
    const blogs = state.me.blogs;
    const blogId = blogs.find((b) => b.id === state.me?.preferences.lastBlogId)?.id ?? blogs[0]?.id;
    if (blogId) navigate('posts', { blogId }, { replace: true });
    else navigate('blogs', {}, { replace: true });
  }, [state, route?.key, navigate]);

  if (state.status === SessionStatus.Loading) return <Loading label={t('common.loading')} />;
  if (route?.key === 'login' || route?.key === 'register') {
    // IAM-UC-14.CC-4: вошедшего страницы входа и регистрации уводят на главную.
    if (state.status === SessionStatus.Authenticated) {
      return <RedirectHome />;
    }
    return route.key === 'login' ? <LoginPage /> : <RegisterPage />;
  }
  if (state.status !== SessionStatus.Authenticated) return <Loading label={t('common.loading')} />;

  const blogId = route && BLOG_ROUTES.includes(route.key) ? (route.params.blogId ?? null) : null;
  return (
    <AppShellConnected blogId={blogId}>
      {route && blogId ? <BlogArea route={route} blogId={blogId} /> : <GlobalArea route={route} />}
    </AppShellConnected>
  );
}

function RedirectHome(): ReactElement {
  const navigate = useNavigate();
  const { t } = useT();
  useEffect(() => navigate('home', {}, { replace: true }), [navigate]);
  return <Loading label={t('common.loading')} />;
}

function RedirectToSettings({ blogId, section }: { blogId: string; section: SettingsSection }): ReactElement {
  const navigate = useNavigate();
  const { t } = useT();
  useEffect(() => navigate('settings', { blogId, section }, { replace: true }), [navigate, blogId, section]);
  return <Loading label={t('common.loading')} />;
}

function NotFound(): ReactElement {
  const { t } = useT();
  const navigate = useNavigate();
  return <EmptyState title={t('common.notFoundTitle')} text={t('common.notFoundText')} action={<Button onClick={() => navigate('home')}>{t('common.toHome')}</Button>} />;
}

function GlobalArea({ route }: { route: RouteMatch<RouteKey> | null }): ReactElement {
  const { session } = useServices();
  switch (route?.key) {
    case 'home':
    case 'blogs':
      return <BlogsPage />;
    case 'profile':
      return <ProfilePage />;
    case 'adminUsers':
      return session.isAdmin() ? <UsersAdminPage /> : <NotFound />;
    case 'adminOverview':
      return session.isAdmin() ? <AdminOverviewPage /> : <NotFound />;
    case 'adminBlogs':
      return session.isAdmin() ? <AdminBlogsPage /> : <NotFound />;
    case 'adminOnline':
      return session.isAdmin() ? <AdminOnlinePage /> : <NotFound />;
    case 'adminSettings':
      return session.isAdmin() ? <AdminSettingsPage /> : <NotFound />;
    case 'adminApiKeys':
      return session.isAdmin() ? <ApiKeysAdminPage /> : <NotFound />;
    case 'adminPlans':
      return session.isAdmin() ? <PlansAdminPage /> : <NotFound />;
    case 'adminTasks':
      return session.isAdmin() ? <AdminTasksPage /> : <NotFound />;
    case 'adminBackups':
      return session.isAdmin() ? <AdminBackupsPage /> : <NotFound />;
    default:
      return <NotFound />;
  }
}

/** Страницы блога: блог загружается, права роли — в контексте (WS-UC-6, WS-UC-6.CC-1). */
function BlogArea({ route, blogId }: { route: RouteMatch<RouteKey>; blogId: string }): ReactElement {
  const { api, session } = useServices();
  const { t, te } = useT();
  const blog = useResource(() => api.workspaces.get(blogId), [api, blogId]);

  useEffect(() => {
    if (blog.data) void session.rememberBlog(blog.data.id);
  }, [blog.data, session]);

  if (blog.loading && !blog.data) return <Loading label={t('common.loading')} />;
  if (blog.error || !blog.data) return blog.error?.includes('BLOG_NOT_FOUND') ? <NotFound /> : <Alert tone="danger">{te(blog.error)}</Alert>;

  const p = route.params;
  let page: ReactElement;
  switch (route.key) {
    case 'posts':
      page = <PostsPage />;
      break;
    case 'post':
      page = <PostPage key={p.postId} postId={p.postId!} />;
      break;
    case 'media':
      page = <MediaLibraryPage />;
      break;
    case 'mediaItem':
      page = <MediaPage key={p.mediaId} mediaId={p.mediaId!} />;
      break;
    case 'entities':
      page = <EntityListPage key={p.typeId} typeId={p.typeId!} />;
      break;
    case 'entity':
      page = <EntityPage key={`${p.typeId}-${p.entityId}`} typeId={p.typeId!} entityId={p.entityId!} />;
      break;
    case 'queue':
      page = <QueuePage />;
      break;
    case 'calendar':
      page = <CalendarPage />;
      break;
    case 'due':
      page = <DuePage />;
      break;
    case 'publication':
      page = <PublicationPage key={p.publicationId} publicationId={p.publicationId!} />;
      break;
    case 'settings':
      page = <SettingsPage section={p.section as SettingsSection} />;
      break;
    case 'tasks':
      // Задачи блога переехали в раздел настроек (TASK-UC-3): старые ссылки ведут туда.
      page = <RedirectToSettings blogId={blogId} section={SettingsSection.Tasks} />;
      break;
    case 'task':
      page = <BlogTaskPage key={p.taskId} taskId={p.taskId!} />;
      break;
    default:
      page = <NotFound />;
  }
  return (
    <BlogProvider blog={blog.data} reloadBlog={blog.reload}>
      {page}
    </BlogProvider>
  );
}

function SettingsPage({ section }: { section: SettingsSection }): ReactElement {
  const { t } = useT();
  const { session, router, catalogVersion } = useServices();
  const route = useRoute();
  const blogId = route?.params.blogId ?? '';
  const canSettings = session.can(blogId, BlogPermission.ManageSettings);
  const sections: { key: SettingsSection; label: string }[] = [
    { key: SettingsSection.Blog, label: t('workspaces.settings.blog') },
    { key: SettingsSection.Members, label: t('workspaces.settings.members') },
    { key: SettingsSection.Channels, label: t('workspaces.settings.channels') },
    { key: SettingsSection.Schemes, label: t('workspaces.settings.schemes') },
    { key: SettingsSection.Statuses, label: t('workspaces.settings.statuses') },
    { key: SettingsSection.PostFormats, label: t('workspaces.settings.postFormats') },
    { key: SettingsSection.EntityTypes, label: t('workspaces.settings.entityTypes') },
    { key: SettingsSection.PostFields, label: t('workspaces.settings.postFields') },
    { key: SettingsSection.MediaTraffic, label: t('media.traffic.title') },
    { key: SettingsSection.MediaStorage, label: t('media.storage.title') },
    { key: SettingsSection.Tasks, label: t('tasks.page.section') },
  ];
  const content = (): ReactElement => {
    if (section === SettingsSection.Members) return <MembersSection />;
    // TASK-RULE-3: задачи блога видят все его участники.
    if (section === SettingsSection.Tasks) return <BlogTasksSection />;
    if (!canSettings) return <Alert tone="info">{t('workspaces.settings.onlyOwner')}</Alert>;
    switch (section) {
      case SettingsSection.Blog:
        return <BlogSettingsSection />;
      case SettingsSection.Channels:
        return <ChannelsSection />;
      case SettingsSection.Schemes:
        return <SchemesSection />;
      case SettingsSection.Statuses:
        return <StatusesSection />;
      case SettingsSection.PostFormats:
        return <PostFormatsSection />;
      case SettingsSection.EntityTypes:
        return <EntityTypesSection onChanged={() => catalogVersion.set(catalogVersion.get() + 1)} />;
      case SettingsSection.PostFields:
        return <PostFieldsSection />;
      case SettingsSection.MediaTraffic:
        return <MediaTrafficSection />;
      case SettingsSection.MediaStorage:
        return <MediaStorageSection />;
      default:
        return <BlogSettingsSection />;
    }
  };
  return (
    <div className="u-stack-lg">
      <PageHeader title={t('workspaces.settings.title')} />
      <NavTabs
        label={t('workspaces.settings.sections')}
        items={sections.map((item) => {
          const href = router.path('settings', { blogId, section: item.key });
          return { href, label: item.label, current: item.key === section, onNavigate: () => router.navigate(href) };
        })}
      />
      {content()}
    </div>
  );
}
